<!-- src/apps/secret/components/receipt/ShareChannels.vue -->

<script setup lang="ts">
  import { loggingService } from '@/services/logging.service';
  import { sendWhatsAppLink } from '@/services/whatsapp.service';
  import { useApi } from '@/shared/composables/useApi';
  import { onClickOutside } from '@vueuse/core';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { COUNTRY_CODES, DEFAULT_COUNTRY, type CountryCode } from './country-codes';

  // Terminal "share via" panel from the receipt chrome. Two channels:
  //   • WhatsApp — opens an inline country-code + number form, then hands off to
  //     the user's own WhatsApp via a wa.me deep link (no paid provider wired).
  //   • E-mail   — opens an inline recipient field and sends the link
  //     server-side via the platform (Resend). Authenticated owners use the
  //     attributed endpoint; guests use the guest twin (bearer of the receipt).
  // Telegram was removed (delivery option under review, mirrors SecretComposer).
  interface Props {
    link: string;
    /**
     * Receipt identifier (metadata key) used to send the link server-side.
     * Required for the E-mail server-send path.
     */
    notifyIdentifier?: string;
    /**
     * True when the viewer is the authenticated owner — selects the attributed
     * notify endpoint. When false (guest), the guest notify endpoint is used.
     */
    canNotify?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    notifyIdentifier: '',
    canNotify: false,
  });

  const { t } = useI18n();
  const $api = useApi();

  // Which inline panel is open (null = just the three buttons).
  type Channel = 'email' | 'whatsapp';
  const activeChannel = ref<Channel | null>(null);

  const toggleChannel = (channel: Channel) => {
    activeChannel.value = activeChannel.value === channel ? null : channel;
  };

  // ── E-mail ────────────────────────────────────────────────────────────────
  const emailTo = ref('');
  type EmailState = 'idle' | 'sending' | 'sent' | 'error';
  const emailState = ref<EmailState>('idle');

  // Deliberately loose client-side check; the server re-validates with Truemail.
  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  const emailValid = computed(() => isValidEmail(emailTo.value));

  const sendEmail = async () => {
    if (!emailValid.value || emailState.value === 'sending') return;
    if (!props.notifyIdentifier) return; // no receipt id → cannot dispatch

    // Always dispatch server-side (via Resend). Authenticated owners use the
    // attributed endpoint; guests (bearer of the receipt) use the guest twin,
    // which the backend bounds with per-receipt and per-IP caps.
    const endpoint = props.canNotify
      ? `/api/v3/receipt/${props.notifyIdentifier}/notify`
      : `/api/v3/guest/receipt/${props.notifyIdentifier}/notify`;

    emailState.value = 'sending';
    try {
      await $api.post(endpoint, { email: emailTo.value.trim() });
      emailState.value = 'sent';
    } catch (error) {
      loggingService.error(error as Error);
      emailState.value = 'error';
    }
  };

  const resetEmail = () => {
    emailState.value = 'idle';
  };

  // ── WhatsApp ────────────────────────────────────────────────────────────────
  const selectedCountry = ref<CountryCode>(DEFAULT_COUNTRY);
  const waNumber = ref('');
  const waError = ref(false);

  const countryOpen = ref(false);
  const countrySelect = ref<HTMLElement | null>(null);
  onClickOutside(countrySelect, () => {
    countryOpen.value = false;
  });

  const pickCountry = (country: CountryCode) => {
    selectedCountry.value = country;
    countryOpen.value = false;
  };

  const sendWhatsApp = () => {
    const result = sendWhatsAppLink({
      countryCode: selectedCountry.value.dial,
      number: waNumber.value,
      secretUrl: props.link,
    });

    if (!result.waMeUrl) {
      waError.value = true;
      return;
    }
    waError.value = false;
    // Opens the user's WhatsApp (app/web) with the number + link prefilled.
    // Automatic provider delivery (Twilio / Meta) is a future server-side step.
    window.open(result.waMeUrl, '_blank', 'noopener');
  };

  const channelButtonClass = (active: boolean) =>
    active
      ? `border-brand-500/60 text-brand-600 dark:text-brand-500
        dark:border-brand-500/60`
      : `border-day-line text-day-ink-muted hover:bg-day-100 hover:text-day-ink
        dark:border-white/[0.07] dark:text-night-ink-muted
        dark:hover:bg-night-100 dark:hover:text-night-ink`;

  // Shared terminal control classes.
  const FIELD_CLASS =
    'w-full border border-day-line bg-day px-3 py-2 font-mono text-xs ' +
    'text-day-ink placeholder:text-day-ink-faint ' +
    'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
    'dark:border-white/[0.13] dark:bg-night dark:text-night-ink ' +
    'dark:placeholder:text-night-ink-faint';

  const SEND_BTN_CLASS =
    'flex shrink-0 items-center justify-center gap-1.5 border border-brand-600 ' +
    'bg-brand-600 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.08em] ' +
    'text-white transition-colors duration-200 hover:bg-brand-700 ' +
    'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 ' +
    'disabled:cursor-not-allowed disabled:opacity-40 ' +
    'dark:focus:ring-offset-night-100';
</script>

<template>
  <div>
    <!-- Two channel buttons -->
    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <!-- WhatsApp -->
      <!-- prettier-ignore-attribute class -->
      <button
        type="button"
        data-testid="share-whatsapp"
        :aria-expanded="activeChannel === 'whatsapp'"
        @click="toggleChannel('whatsapp')"
        class="flex items-center justify-center gap-2 border px-3 py-2.5
          font-mono text-[10px] tracking-[0.08em] uppercase
          transition-colors duration-200"
        :class="channelButtonClass(activeChannel === 'whatsapp')">
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          class="size-3.5 shrink-0"
          aria-hidden="true">
          <path
            d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
        <span>WhatsApp</span>
      </button>

      <!-- E-mail -->
      <!-- prettier-ignore-attribute class -->
      <button
        type="button"
        data-testid="share-email"
        :aria-expanded="activeChannel === 'email'"
        @click="toggleChannel('email')"
        class="flex items-center justify-center gap-2 border px-3 py-2.5
          font-mono text-[10px] tracking-[0.08em] uppercase
          transition-colors duration-200"
        :class="channelButtonClass(activeChannel === 'email')">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="size-3.5 shrink-0"
          aria-hidden="true">
          <rect
            x="2"
            y="4"
            width="20"
            height="16"
            rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
        <span>E-mail</span>
      </button>
    </div>

    <!-- ── E-mail inline panel ─────────────────────────────────────────────── -->
    <!-- prettier-ignore-attribute class -->
    <div
      v-if="activeChannel === 'email'"
      data-testid="share-email-panel"
      class="mt-2 border border-day-line bg-day-100 p-3
        dark:border-white/[0.07] dark:bg-night-200/60">
      <template v-if="emailState === 'sent'">
        <div
          data-testid="share-email-sent"
          class="flex items-center gap-2 py-1 font-mono text-[11px] tracking-[0.08em] text-emerald-600
            uppercase dark:text-emerald-400">
          <span
            class="inline-block size-[5px] shrink-0 rounded-full bg-emerald-500"
            aria-hidden="true"></span>
          {{ t('receipt_terminal.share_email_sent') }}
        </div>
      </template>

      <template v-else>
        <label
          class="mb-1.5 block font-mono text-[9px] tracking-[0.08em] text-day-ink-muted
            uppercase dark:text-night-ink-muted"
          for="share-email-input">
          {{ t('receipt_terminal.share_email_label') }}
        </label>
        <div class="flex items-stretch gap-2">
          <input
            id="share-email-input"
            v-model="emailTo"
            type="email"
            inputmode="email"
            autocomplete="email"
            data-testid="share-email-input"
            :placeholder="t('web.COMMON.email_placeholder')"
            :class="FIELD_CLASS"
            @input="resetEmail"
            @keyup.enter="sendEmail" />
          <!-- prettier-ignore-attribute class -->
          <button
            type="button"
            data-testid="share-email-send"
            :disabled="!emailValid || emailState === 'sending'"
            :class="SEND_BTN_CLASS"
            @click="sendEmail">
            {{
              emailState === 'sending'
                ? t('web.LABELS.sending_ellipses')
                : t('receipt_terminal.share_email_send')
            }}
          </button>
        </div>
        <p
          v-if="emailState === 'error'"
          data-testid="share-email-error"
          class="mt-1.5 font-mono text-[10px] text-red-600 dark:text-[#FF5555]">
          {{ t('receipt_terminal.share_email_error') }}
        </p>
      </template>
    </div>

    <!-- ── WhatsApp inline panel ───────────────────────────────────────────── -->
    <!-- prettier-ignore-attribute class -->
    <div
      v-if="activeChannel === 'whatsapp'"
      data-testid="share-whatsapp-panel"
      class="mt-2 border border-day-line bg-day-100 p-3
        dark:border-white/[0.07] dark:bg-night-200/60">
      <div class="flex items-stretch gap-2">
        <!-- Country selector -->
        <div
          ref="countrySelect"
          class="relative shrink-0">
          <!-- prettier-ignore-attribute class -->
          <button
            type="button"
            data-testid="share-whatsapp-country"
            :aria-label="t('receipt_terminal.share_whatsapp_country')"
            :aria-expanded="countryOpen"
            @click="countryOpen = !countryOpen"
            class="flex h-full items-center gap-1.5 border border-day-line bg-day px-2.5 py-2
              font-mono text-xs text-day-ink transition-colors
              hover:border-brand-500/40 focus:border-brand-500 focus:outline-none
              dark:border-white/[0.13] dark:bg-night dark:text-night-ink">
            <span
              class="text-sm leading-none"
              aria-hidden="true">{{ selectedCountry.flag }}</span>
            <span class="tabular-nums">+{{ selectedCountry.dial }}</span>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="size-3 shrink-0 opacity-60"
              aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>

          <!-- Dropdown -->
          <!-- prettier-ignore-attribute class -->
          <ul
            v-if="countryOpen"
            data-testid="share-whatsapp-country-list"
            class="absolute top-full left-0 z-20 mt-1 max-h-56 w-60 overflow-y-auto
              border border-day-line bg-day py-1 shadow-lg
              dark:border-white/[0.13] dark:bg-night-100">
            <li
              v-for="country in COUNTRY_CODES"
              :key="country.iso">
              <!-- prettier-ignore-attribute class -->
              <button
                type="button"
                @click="pickCountry(country)"
                class="flex w-full items-center gap-2 px-3 py-1.5 text-left
                  font-mono text-xs text-day-ink transition-colors
                  hover:bg-day-200 dark:text-night-ink dark:hover:bg-night-200"
                :class="
                  country.iso === selectedCountry.iso
                    ? 'bg-day-200 dark:bg-night-200'
                    : ''
                ">
                <span
                  class="text-sm leading-none"
                  aria-hidden="true">{{ country.flag }}</span>
                <span class="min-w-0 flex-1 truncate">{{ country.name }}</span>
                <span class="text-day-ink-faint tabular-nums dark:text-night-ink-faint">
                  +{{ country.dial }}
                </span>
              </button>
            </li>
          </ul>
        </div>

        <!-- Number -->
        <input
          v-model="waNumber"
          type="tel"
          inputmode="tel"
          data-testid="share-whatsapp-number"
          :placeholder="t('receipt_terminal.share_whatsapp_number')"
          :class="FIELD_CLASS"
          @input="waError = false"
          @keyup.enter="sendWhatsApp" />

        <!-- Send -->
        <!-- prettier-ignore-attribute class -->
        <button
          type="button"
          data-testid="share-whatsapp-send"
          :class="SEND_BTN_CLASS"
          @click="sendWhatsApp">
          {{ t('receipt_terminal.share_email_send') }}
        </button>
      </div>

      <p
        v-if="waError"
        data-testid="share-whatsapp-error"
        class="mt-1.5 font-mono text-[10px] text-red-600 dark:text-[#FF5555]">
        {{ t('receipt_terminal.share_whatsapp_error') }}
      </p>
      <p
        v-else
        class="mt-1.5 font-mono text-[10px] text-day-ink-muted dark:text-night-ink-muted">
        {{ t('receipt_terminal.share_whatsapp_note') }}
      </p>
    </div>
  </div>
</template>

